import { useState } from "react";
import { CHART_OF_ACCOUNTS, MOCK_JOURNAL_ENTRIES, JournalEntry, JournalLine, CompanyType, Account } from "../../../mocks/companyTypes";
import { useRole } from "../../../contexts/RoleContext";

type ViewMode = "list" | "new" | "ledger";

function getAccountsForCompany(companyType: CompanyType): Account[] {
  return CHART_OF_ACCOUNTS.filter(a =>
    a.companyTypes === 'all' || (a.companyTypes as CompanyType[]).includes(companyType)
  );
}

const CATEGORY_COLORS: Record<string, string> = {
  자산: "text-[#7A9BFF]",
  부채: "text-white/40",
  자본: "text-[#888888]",
  수익: "text-[#CCCCCC]",
  비용: "text-white/30",
};

export default function JournalEntryPanel() {
  const { companyType } = useRole();
  const accounts = getAccountsForCompany(companyType);
  const [mode, setMode] = useState<ViewMode>("list");
  const [entries, setEntries] = useState<JournalEntry[]>(MOCK_JOURNAL_ENTRIES);
  const [selected, setSelected] = useState<JournalEntry | null>(null);
  const [notification, setNotification] = useState<string | null>(null);

  // 신규 전표 상태
  const [newDate, setNewDate] = useState("2026.03.27");
  const [newType, setNewType] = useState<JournalEntry["type"]>("대체전표");
  const [newDesc, setNewDesc] = useState("");
  const [lines, setLines] = useState<JournalLine[]>([
    { accountCode: "", accountName: "", debit: 0, credit: 0, memo: "" },
    { accountCode: "", accountName: "", debit: 0, credit: 0, memo: "" },
  ]);

  const totalDebit = lines.reduce((s, l) => s + (l.debit || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (l.credit || 0), 0);
  const isBalanced = totalDebit > 0 && totalDebit === totalCredit;

  const updateLine = (idx: number, field: keyof JournalLine, value: string | number) => {
    setLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      if (field === "accountCode") {
        const acc = accounts.find(a => a.code === value);
        return { ...l, accountCode: value as string, accountName: acc?.name ?? "" };
      }
      return { ...l, [field]: value };
    }));
  };

  const addLine = () => setLines(prev => [...prev, { accountCode: "", accountName: "", debit: 0, credit: 0, memo: "" }]);
  const removeLine = (idx: number) => {
    if (lines.length <= 2) return;
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = (status: "확정" | "임시저장") => {
    if (!newDesc.trim()) { setNotification("적요를 입력해주세요."); setTimeout(() => setNotification(null), 2000); return; }
    if (status === "확정" && !isBalanced) { setNotification("차변과 대변 합계가 일치해야 합니다."); setTimeout(() => setNotification(null), 3000); return; }
    const newEntry: JournalEntry = {
      id: `je${Date.now()}`,
      date: newDate,
      type: newType,
      number: `JE-${newDate.replace(/\./g, "").slice(0, 8)}-${String(entries.length + 1).padStart(3, "0")}`,
      description: newDesc,
      lines: lines.filter(l => l.accountCode),
      totalDebit,
      totalCredit,
      createdBy: "재무 담당",
      status,
    };
    setEntries(prev => [newEntry, ...prev]);
    setNewDesc(""); setLines([{ accountCode: "", accountName: "", debit: 0, credit: 0, memo: "" }, { accountCode: "", accountName: "", debit: 0, credit: 0, memo: "" }]);
    setMode("list");
    setNotification(status === "확정" ? "전표가 확정 처리되었습니다." : "임시저장 되었습니다.");
    setTimeout(() => setNotification(null), 3000);
  };

  // 총계정원장: 계정별 합계
  const ledgerData = accounts.filter(a => a.category === "수익" || a.category === "비용" || a.category === "자산").slice(0, 12).map(acc => {
    const relevant = entries.flatMap(e => e.lines).filter(l => l.accountCode === acc.code);
    const totalD = relevant.reduce((s, l) => s + l.debit, 0);
    const totalC = relevant.reduce((s, l) => s + l.credit, 0);
    return { ...acc, totalDebit: totalD, totalCredit: totalC, balance: totalD - totalC };
  }).filter(a => a.totalDebit > 0 || a.totalCredit > 0);

  return (
    <div className="space-y-4">
      {notification && (
        <div className="px-4 py-2.5 rounded-lg bg-[#4B6CF7]/10 border border-[#4B6CF7]/20 text-[#7A9BFF] text-xs flex items-center gap-2">
          <i className="ri-check-line"></i>{notification}
        </div>
      )}

      {/* Mode tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0.5 bg-white/[0.03] rounded-xl p-1 border border-white/[0.05]">
          {([["list", "ri-list-check", "전표 목록"], ["new", "ri-add-circle-line", "전표 입력"], ["ledger", "ri-book-2-line", "총계정원장"]] as const).map(([id, icon, label]) => (
            <button key={id} type="button" onClick={() => setMode(id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                mode === id ? "bg-[#4B6CF7]/15 text-[#7A9BFF] border border-[#4B6CF7]/20" : "text-[#444444] hover:text-[#888888]"
              }`}>
              <i className={`${icon} text-sm`}></i>{label}
            </button>
          ))}
        </div>
        {mode === "list" && (
          <button type="button" onClick={() => setMode("new")}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#4B6CF7]/10 text-[#7A9BFF] border border-[#4B6CF7]/20 cursor-pointer hover:bg-[#4B6CF7]/20 transition-colors whitespace-nowrap">
            <i className="ri-add-line mr-1"></i>신규 전표
          </button>
        )}
      </div>

      {/* 전표 목록 */}
      {mode === "list" && (
        <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/[0.04]">
                {["전표번호", "일자", "유형", "적요", "차변", "대변", "상태"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[#3D3D3D] font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.03]">
              {entries.map(e => (
                <tr key={e.id} onClick={() => { setSelected(selected?.id === e.id ? null : e); }}
                  className="hover:bg-white/[0.02] cursor-pointer transition-colors">
                  <td className="px-4 py-3 text-[#555555] font-mono text-[10px]">{e.number}</td>
                  <td className="px-4 py-3 text-[#555555]">{e.date}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      e.type === "입금전표" ? "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" :
                      e.type === "출금전표" ? "text-white/40 border-white/10" :
                      "text-[#555555] border-white/[0.05]"
                    }`}>{e.type}</span>
                  </td>
                  <td className="px-4 py-3 text-[#CCCCCC]">{e.description}</td>
                  <td className="px-4 py-3 text-[#7A9BFF] tabular-nums">{e.totalDebit.toLocaleString()}만</td>
                  <td className="px-4 py-3 text-white/40 tabular-nums">{e.totalCredit.toLocaleString()}만</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      e.status === "확정" ? "text-[#888888] border-white/[0.08]" : "text-[#555555] border-white/[0.04]"
                    }`}>{e.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* 상세 행 */}
          {selected && (
            <div className="border-t border-white/[0.06] bg-[#0C0C0E] p-4">
              <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-3">전표 상세 — {selected.number}</div>
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    {["계정과목", "차변", "대변", "적요"].map(h => (
                      <th key={h} className="py-1.5 text-left text-[#555555] font-medium pr-6">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selected.lines.map((l, i) => (
                    <tr key={i}>
                      <td className="py-1.5 pr-6 text-[#888888]">{l.accountCode} {l.accountName}</td>
                      <td className="py-1.5 pr-6 text-[#7A9BFF] tabular-nums">{l.debit > 0 ? `${l.debit.toLocaleString()}만` : "-"}</td>
                      <td className="py-1.5 pr-6 text-white/40 tabular-nums">{l.credit > 0 ? `${l.credit.toLocaleString()}만` : "-"}</td>
                      <td className="py-1.5 text-[#444444]">{l.memo}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-white/[0.04]">
                    <td className="pt-2 text-[#3D3D3D] font-medium">합계</td>
                    <td className="pt-2 text-[#7A9BFF] font-semibold tabular-nums">{selected.totalDebit.toLocaleString()}만</td>
                    <td className="pt-2 text-white/40 font-semibold tabular-nums">{selected.totalCredit.toLocaleString()}만</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 전표 입력 */}
      {mode === "new" && (
        <div className="bg-[#111112] border border-white/[0.06] rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[#3D3D3D] text-[10px] uppercase tracking-widest block mb-1.5">일자</label>
              <input type="text" value={newDate} onChange={e => setNewDate(e.target.value)}
                className="w-full bg-[#0C0C0E] border border-white/[0.08] rounded-lg px-3 py-2 text-[#F2F2F2] text-sm outline-none focus:border-[#4B6CF7]/40" />
            </div>
            <div>
              <label className="text-[#3D3D3D] text-[10px] uppercase tracking-widest block mb-1.5">전표 유형</label>
              <select value={newType} onChange={e => setNewType(e.target.value as JournalEntry["type"])}
                className="w-full bg-[#0C0C0E] border border-white/[0.08] rounded-lg px-3 py-2 text-[#F2F2F2] text-sm outline-none focus:border-[#4B6CF7]/40 cursor-pointer">
                <option value="입금전표">입금전표</option>
                <option value="출금전표">출금전표</option>
                <option value="대체전표">대체전표</option>
              </select>
            </div>
            <div>
              <label className="text-[#3D3D3D] text-[10px] uppercase tracking-widest block mb-1.5">적요</label>
              <input type="text" value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="거래 내용 입력"
                className="w-full bg-[#0C0C0E] border border-white/[0.08] rounded-lg px-3 py-2 text-[#F2F2F2] text-sm outline-none focus:border-[#4B6CF7]/40 placeholder-[#555555]" />
            </div>
          </div>

          {/* 분개 라인 */}
          <div className="bg-[#0C0C0E] rounded-xl border border-white/[0.05] overflow-hidden">
            <div className="grid grid-cols-[2fr_1fr_1fr_2fr_auto] gap-0 border-b border-white/[0.05] px-4 py-2">
              {["계정과목", "차변(만원)", "대변(만원)", "적요", ""].map(h => (
                <div key={h} className="text-[#555555] text-[10px] font-medium pr-2">{h}</div>
              ))}
            </div>
            {lines.map((line, idx) => (
              <div key={idx} className="grid grid-cols-[2fr_1fr_1fr_2fr_auto] gap-0 px-4 py-2 border-b border-white/[0.03] items-center">
                <select value={line.accountCode} onChange={e => updateLine(idx, "accountCode", e.target.value)}
                  className="bg-transparent border border-white/[0.06] rounded px-2 py-1.5 text-[#CCCCCC] text-xs outline-none focus:border-[#4B6CF7]/30 mr-2 cursor-pointer">
                  <option value="">계정 선택</option>
                  {(["자산", "부채", "자본", "수익", "비용"] as const).map(cat => (
                    <optgroup key={cat} label={cat}>
                      {accounts.filter(a => a.category === cat).map(a => (
                        <option key={a.code} value={a.code}>{a.code} {a.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <input type="number" value={line.debit || ""} onChange={e => updateLine(idx, "debit", parseInt(e.target.value) || 0)}
                  placeholder="0"
                  className="bg-transparent border border-white/[0.06] rounded px-2 py-1.5 text-[#7A9BFF] text-xs outline-none focus:border-[#4B6CF7]/30 tabular-nums mr-2 w-full" />
                <input type="number" value={line.credit || ""} onChange={e => updateLine(idx, "credit", parseInt(e.target.value) || 0)}
                  placeholder="0"
                  className="bg-transparent border border-white/[0.06] rounded px-2 py-1.5 text-white/40 text-xs outline-none focus:border-[#4B6CF7]/30 tabular-nums mr-2 w-full" />
                <input type="text" value={line.memo} onChange={e => updateLine(idx, "memo", e.target.value)}
                  placeholder="비고"
                  className="bg-transparent border border-white/[0.06] rounded px-2 py-1.5 text-[#555555] text-xs outline-none focus:border-[#4B6CF7]/30 mr-2" />
                <button type="button" onClick={() => removeLine(idx)}
                  className="w-6 h-6 flex items-center justify-center text-[#444444] hover:text-[#555555] transition-colors cursor-pointer shrink-0">
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            ))}
            <div className="px-4 py-2 flex items-center justify-between">
              <button type="button" onClick={addLine}
                className="text-[#555555] text-xs hover:text-[#666666] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
                <i className="ri-add-line"></i>행 추가
              </button>
              <div className="flex items-center gap-4 text-xs">
                <span className="text-[#555555]">차변 합계 <span className="text-[#7A9BFF] tabular-nums">{totalDebit.toLocaleString()}만</span></span>
                <span className="text-[#555555]">대변 합계 <span className="text-white/40 tabular-nums">{totalCredit.toLocaleString()}만</span></span>
                {totalDebit > 0 && (
                  <span className={`font-semibold ${isBalanced ? "text-[#7A9BFF]" : "text-white/30"}`}>
                    {isBalanced ? "✓ 대차 일치" : `차액 ${Math.abs(totalDebit - totalCredit).toLocaleString()}만`}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={() => handleSubmit("확정")} disabled={!isBalanced}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                isBalanced ? "bg-[#4B6CF7] text-white hover:bg-[#5B7CF7] cursor-pointer" : "bg-white/[0.04] text-[#2A2A2A] cursor-not-allowed"
              }`}>
              전표 확정
            </button>
            <button type="button" onClick={() => handleSubmit("임시저장")}
              className="px-5 py-2.5 rounded-lg text-sm font-medium border border-white/[0.08] text-[#666666] hover:text-[#AAAAAA] hover:border-white/[0.15] transition-all cursor-pointer whitespace-nowrap">
              임시저장
            </button>
            <button type="button" onClick={() => setMode("list")}
              className="px-5 py-2.5 rounded-lg text-sm font-medium text-[#3D3D3D] hover:text-[#666666] transition-all cursor-pointer whitespace-nowrap">
              취소
            </button>
          </div>
        </div>
      )}

      {/* 총계정원장 */}
      {mode === "ledger" && (
        <div className="space-y-3">
          <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/[0.05]">
              <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">총계정원장 (잔액 있는 계정)</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/[0.04]">
                  {["코드", "계정과목", "구분", "차변 합계", "대변 합계", "잔액"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[#3D3D3D] font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.03]">
                {ledgerData.length > 0 ? ledgerData.map(a => (
                  <tr key={a.code} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-[#3D3D3D] font-mono">{a.code}</td>
                    <td className="px-4 py-3 text-[#CCCCCC]">{a.name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] ${CATEGORY_COLORS[a.category]}`}>{a.category}</span>
                    </td>
                    <td className="px-4 py-3 text-[#7A9BFF] tabular-nums">{a.totalDebit.toLocaleString()}만</td>
                    <td className="px-4 py-3 text-white/40 tabular-nums">{a.totalCredit.toLocaleString()}만</td>
                    <td className="px-4 py-3 text-[#F2F2F2] font-semibold tabular-nums">
                      {a.balance >= 0 ? "+" : ""}{a.balance.toLocaleString()}만
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[#555555]">전표를 입력하면 원장이 자동 생성됩니다</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
